import { useState, type ImgHTMLAttributes } from 'react';
import { cn } from '@/shared/utils/cn';
import { buildSrcSet } from '@/shared/utils/srcset';

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src: string;
  fill?: boolean;
  aspect?: string; // e.g. "16/9", "1/1", "9/16"
};

/**
 * Skeleton overlay shown while the image is fetching/decoding. A subtle
 * pulsing surface that fades out once the image fires its `load` event.
 * Gives users a visible "this area is loading" signal instead of a black
 * void on slow networks.
 */
function Skeleton({ visible }: { visible: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'absolute inset-0 bg-gradient-to-br from-white/[0.04] via-white/[0.08] to-white/[0.04] animate-pulse pointer-events-none transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}

export function SmartImage({ src, fill, aspect, className, alt = '', onLoad, ...rest }: Props) {
  const srcSet = buildSrcSet(src);
  const sizes = rest.sizes ?? '(max-width: 640px) 100vw, 640px';
  const [loaded, setLoaded] = useState(false);

  if (fill) {
    return (
      <div
        className={cn('relative h-full w-full overflow-hidden', className)}
        style={aspect ? { aspectRatio: aspect, height: 'auto' } : undefined}
      >
        <Skeleton visible={!loaded} />
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            setLoaded(true);
            onLoad?.(e);
          }}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity duration-200',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
          {...rest}
        />
      </div>
    );
  }
  // Non-fill mode: skeleton is more awkward because we don't own a wrapper.
  // Just render the <img> and let the parent provide its own placeholder if
  // it wants one. (Most usages in this codebase use fill mode.)
  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={(e) => {
        setLoaded(true);
        onLoad?.(e);
      }}
      className={className}
      {...rest}
    />
  );
}
